import { useCallback, useEffect, useState } from "react";

import { SIDEBAR_STORAGE_KEY } from "@/core/constants";

const COLLAPSED = "collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === COLLAPSED;
  } catch {
    return false;
  }
}

/** Typing in a field keeps the shortcut's usual meaning there (e.g. bold in a text box). */
function isTyping(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
  );
}

/** "⌘B" on Apple keyboards, "Ctrl B" elsewhere. */
export const SIDEBAR_SHORTCUT =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘B" : "Ctrl B";

/**
 * The desktop sidebar's collapsed state: remembered in this browser, toggled by the edge button or ⌘B / Ctrl B
 * (the common shortcut for a sidebar).
 */
export function useSidebar() {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = useCallback(() => setCollapsed((current) => !current), []);

  useEffect(() => {
    try {
      if (collapsed) localStorage.setItem(SIDEBAR_STORAGE_KEY, COLLAPSED);
      else localStorage.removeItem(SIDEBAR_STORAGE_KEY);
    } catch {
      // Storage blocked (private window): it just isn't remembered.
    }
  }, [collapsed]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "b" || !(event.metaKey || event.ctrlKey)) return;
      // Held keys repeat: one press is one toggle.
      if (event.repeat || event.shiftKey || event.altKey || isTyping(event.target)) return;
      event.preventDefault();
      toggle();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle]);

  return { collapsed, toggle };
}
